import { autorun } from "mobx";
import {
  ItemReOrderIntervals,
  PricingModel,
  ProductDetailDataKeys,
  ProductPricingPageDataKeys,
} from "../enums";
import {
  CustomPricingInputModel,
  PackagePricingInputDataModel,
  PackagePricingInputModel,
  PriceTierInputData,
  PriceTierInputOptions,
  StandardPricingInputModel,
} from "./PriceInputs.model";
import {
  CoreModel,
  DecimalRegex,
  getNewErrors,
  InputErrorModel,
  InputFieldModel,
  InputType,
  UtilityMethods,
} from "@mindmemobile/mm-shared-global";
import { IAPIReOrderSettings } from "../interfaces/IAPIProductModels";
import { IAPIInventorySettings } from "../interfaces/IAPIDonationAndItemModel";

export abstract class ProductInputs {
  public static getDetailForm(): InputFieldModel[] {
    return [
      new InputFieldModel({
        id: "name",
        dataKey: ProductDetailDataKeys.Name,
        name: "Name",
        maxLength: 200,
        type: InputType.TEXT,
        isRequired: true,
        placeHolder: "Name",
        className: "styled-input prod-name-input",
      }),
      new InputFieldModel({
        id: "status",
        dataKey: ProductDetailDataKeys.Status,
        name: "Status",
        type: InputType.SWITCH,
        isRequired: true,
        value: true,
        isConditionalInput: true,
        placeHolder: "Status",
      }),
      new InputFieldModel({
        id: "name",
        dataKey: ProductDetailDataKeys.Description,
        name: "Description",
        maxLength: 200,
        type: InputType.EDITOR,
        placeHolder: "Description",
        className: "product-detail-editor",
      }),
      new InputFieldModel({
        id: "category",
        dataKey: ProductDetailDataKeys.Category,
        name: "Category",
        maxLength: 200,
        type: InputType.SEARCHABLE_SELECT,
        placeHolder: "Category",
      }),
      new InputFieldModel({
        id: "media",
        dataKey: ProductDetailDataKeys.Media,
        name: "Media",
        maxLength: 200,
        type: InputType.TEXT,
        // isConditionalInput: true,
        placeHolder: "Media",
      }),
      new InputFieldModel({
        id: "chkVariants",
        dataKey: ProductDetailDataKeys.HasVariants,
        name: "Variants",
        label: "Create Variants by Attribute",
        type: InputType.CHECK_BOX,
        groupId: "advanced",
        value: false,
        placeHolder: "Variants",
      }),
      new InputFieldModel({
        id: "chkSurcharge",
        dataKey: ProductDetailDataKeys.Surcharge,
        name: "Allow",
        label: "Surcharge",
        type: InputType.CHECK_BOX,
        groupId: "advanced",
        value: false,
      }),
      new InputFieldModel({
        id: "chkDiscount",
        dataKey: ProductDetailDataKeys.Discount,
        name: "",
        label: "Discount",
        type: InputType.CHECK_BOX,
        groupId: "advanced",
        value: false,
        className: "chkbox-no-margin",
      }),
      new InputFieldModel({
        id: "chkReview",
        dataKey: ProductDetailDataKeys.Review,
        name: "",
        label: "Reviews",
        type: InputType.CHECK_BOX,
        groupId: "advanced",
        value: false,
        className: "chkbox-no-margin",
      }),
      new InputFieldModel({
        id: "chkReturn",
        dataKey: ProductDetailDataKeys.Return,
        name: "",
        label: "Return",
        type: InputType.CHECK_BOX,
        value: false,
        requiredChildInputDataKeys: [ProductDetailDataKeys.ReturnDays],
        groupId: "advanced",
        className: "chkbox-no-margin",
      }),
      new InputFieldModel({
        id: "txtReturnDays",
        dataKey: ProductDetailDataKeys.ReturnDays,
        name: "days",
        placeHolder: "#",
        type: InputType.NUMBER,
        maxLength: 200,
        minValue: 1,
        maxValue: 60,
        isConditionalInput: true,
        groupId: "advanced",
      }),

      new InputFieldModel({
        id: "txtSKU",
        dataKey: ProductDetailDataKeys.Sku,
        name: "SKU",
        type: InputType.TEXT,
        maxLength: 200,
        groupId: "advanced",
        placeHolder: "SKU",
        className: "styled-input barcode-input",
      }),
      new InputFieldModel({
        id: "txtBarCode",
        dataKey: ProductDetailDataKeys.BarCode,
        name: "Bar Code",
        type: InputType.TEXT,
        maxLength: 200,
        groupId: "advanced",
        placeHolder: "Barcode (UPC, ISBN...)",
        className: "styled-input barcode-input",
      }),
    ];
  }
  public static getPricingPageForm(): InputFieldModel[] {
    return [
      new InputFieldModel({
        id: "ddlPricingOption",
        dataKey: ProductPricingPageDataKeys.PricingModel,
        name: "Pricing Option",
        type: InputType.SELECT,
        isRequired: true,
        placeHolder: "Pricing Option",
        className: "pricing-option-dropdown",
      }),
      new InputFieldModel({
        id: "chkAllowReOrder",
        dataKey: ProductPricingPageDataKeys.AllowReOrder,
        name: "ReOrder",
        label: "Allow customers to purchase on a Subscription",
        type: InputType.CHECK_BOX,
        value: false,
        requiredChildInputDataKeys: [
          ProductPricingPageDataKeys.ReOrderIntervals,
          ProductPricingPageDataKeys.ReOrderDiscountPercentage,
        ],
        groupId: "reorderAndSave",
        placeHolder: "ReOrder",
      }),
      new InputFieldModel({
        id: "ddlReorderFrequencies",
        dataKey: ProductPricingPageDataKeys.ReOrderIntervals,
        name: "Re-order Frequencies",
        label: "Available Reorder Frequencies",
        type: InputType.MULTI_SELECT,
        isRequired: false,
        isConditionalInput: true,
        groupId: "reorderAndSave",
        classNamePrefix: "prc-multiselect",
        placeHolder: "Re-order Frequencies",
      }),
      new InputFieldModel({
        id: "txtReOrderedDicount",
        dataKey: ProductPricingPageDataKeys.ReOrderDiscountPercentage,
        name: "Discount offered",
        type: InputType.TEXT,
        isRequired: true,
        regex: DecimalRegex,
        isConditionalInput: true,
        groupId: "reorderAndSave",
        placeHolder: "0.00",
        validateFunction: (value: string | number | boolean | string[]) => {
          if (UtilityMethods.validateDecimalValue(value.toString(), true)) {
            if (parseFloat(value.toString()) > 100) {
              return "Discount should be less than 100";
            }
            return "";
          } else {
            return "Please enter a valid number";
          }
        },
      }),
      new InputFieldModel({
        id: "chkInventory",
        dataKey: ProductPricingPageDataKeys.IsTrackInventory,
        name: "Track Inventory",
        label: "Track Inventory",
        type: InputType.CHECK_BOX,
        isRequired: false,
        value: false,
        requiredChildInputDataKeys: [
          ProductPricingPageDataKeys.AvailableInventory,
        ],
        groupId: "advanced",
        placeHolder: "Track Inventory",
      }),
      new InputFieldModel({
        id: "txtAvailableUnits",
        dataKey: ProductPricingPageDataKeys.AvailableInventory,
        name: "Available Inventory",
        label: "Available Inventory",
        placeHolder: "#Available",
        type: InputType.NUMBER,
        isRequired: false,
        isConditionalInput: true,
        groupId: "advanced",
        validateFunction: (value: string | number | boolean | string[]) => {
          if (UtilityMethods.validateDecimalValue(value.toString(), true)) {
            return "";
          } else {
            return "Please enter a valid number";
          }
        },
      }),
      new InputFieldModel({
        id: "chkSellOutOfStock",
        dataKey: ProductPricingPageDataKeys.IsSellOnOutOfStock,
        name: "Sell out of stock",
        label: "Continue selling when out of stock",
        type: InputType.CHECK_BOX,
        value: false,
        isRequired: false,
        isConditionalInput: true,
        groupId: "advanced",
        placeHolder: "Sell out of stock",
      }),
      new InputFieldModel({
        id: "chkIsPromote",
        dataKey: ProductPricingPageDataKeys.IsPromote,
        name: "Promote",
        label: "Promote when inventory is low  (< 10 units)",
        type: InputType.CHECK_BOX,
        value: false,
        isRequired: false,
        placeHolder: "#",
        groupId: "advanced",
        isConditionalInput: true,
      }),
      new InputFieldModel({
        id: "chkLimitQuantity",
        dataKey: ProductPricingPageDataKeys.IsLimitPurchaseQuantity,
        name: "Limit quantity",
        label: "Limit Quantity Sold",
        type: InputType.CHECK_BOX,
        value: false,
        isRequired: false,
        requiredChildInputDataKeys: [ProductPricingPageDataKeys.QuantityLimit],
        groupId: "advanced",
        placeHolder: "Limit quantity",
      }),
      new InputFieldModel({
        id: "txtNumberOfUnits",
        dataKey: ProductPricingPageDataKeys.QuantityLimit,
        name: "Units",
        label: "Limit quantity that can be purchased at one time.",
        type: InputType.NUMBER,
        isRequired: false,
        isConditionalInput: true,
        groupId: "advanced",
        placeHolder: "# Units",
        validateFunction: (value: string | number | boolean | string[]) => {
          if (UtilityMethods.validateDecimalValue(value.toString(), true)) {
            return "";
          } else {
            return "Please enter a valid number";
          }
        },
      }),
    ];
  }
}

export class ProductPricingPageInputModel extends CoreModel {
  id: string;
  pricingModel: keyof typeof PricingModel;
  inventorySettings: ProductInventorySettingsInputModel =
    new ProductInventorySettingsInputModel();
  volumePricing: PriceTierInputData;
  tieredPricing: PriceTierInputData;
  packagePricing: PackagePricingInputModel = new PackagePricingInputModel();
  customPricing: CustomPricingInputModel = new CustomPricingInputModel();
  standardPricing: StandardPricingInputModel = new StandardPricingInputModel();
  errors: InputErrorModel<ProductPricingPageInputModel>[];
  reOrderSettings: ProductReOrderSettingsInputModel;
  constructor(data?: Partial<ProductPricingPageInputModel>) {
    super();
    this.pricingModel = data?.pricingModel || PricingModel.Standard;
    this.id = data?.id || "";

    this.packagePricing = new PackagePricingInputModel(data?.packagePricing);
    this.customPricing = new CustomPricingInputModel(data?.customPricing);
    this.standardPricing = new StandardPricingInputModel(data?.standardPricing);

    if (!data || !data.packagePricing) {
      this.packagePricing = new PackagePricingInputModel({
        showCost: false,
        showOriginalPrice: false,
        options: [new PackagePricingInputDataModel()],
      });
    }

    if (!data || !data.volumePricing) {
      this.volumePricing = new PriceTierInputData({
        options: [
          new PriceTierInputOptions({
            startValue: 1,
            endValue: "1",
            discountPercentage: "0",
          }),
          new PriceTierInputOptions({ startValue: 2, endValue: "∞" }),
        ],
      });
    } else {
      this.volumePricing = new PriceTierInputData(data.volumePricing);
    }

    if (!data || !data.tieredPricing) {
      this.tieredPricing = new PriceTierInputData({
        options: [
          new PriceTierInputOptions({
            startValue: 1,
            endValue: "1",
            discountPercentage: "0",
          }),
          new PriceTierInputOptions({ startValue: 2, endValue: "∞" }),
        ],
      });
    } else {
      this.tieredPricing = new PriceTierInputData(data.tieredPricing);
    }

    this.inventorySettings = new ProductInventorySettingsInputModel(
      data?.inventorySettings
    );

    this.reOrderSettings = new ProductReOrderSettingsInputModel(
      data?.reOrderSettings
    );

    this.errors = data?.errors || [];
    autorun(() => {
      this.validate(true);
    });
  }

  hasError(key: keyof ProductPricingPageInputModel): boolean {
    return this.errors.some((error) => error.field === key);
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<ProductPricingPageInputModel>[] = [];
    switch (PricingModel[this.pricingModel as keyof typeof PricingModel]) {
      case PricingModel.Volume:
        const isValidVolumePricing = this.volumePricing.validate(isAutoRun);
        if (!isValidVolumePricing) {
          this.addError("volumePricing", newErrors);
        }
        break;
      case PricingModel.Tiered:
        if (!this.tieredPricing.validate(isAutoRun)) {
          this.addError("tieredPricing", newErrors);
        }
        break;
      case PricingModel.Package:
        if (!this.packagePricing.validate(isAutoRun)) {
          this.addError("packagePricing", newErrors);
        }
        break;
      case PricingModel.Custom:
        if (!this.customPricing.validate(isAutoRun)) {
          this.addError("customPricing", newErrors);
        }
        break;
      case PricingModel.Standard:
        if (!this.standardPricing.validate(isAutoRun)) {
          this.addError("standardPricing", newErrors);
        }
        break;
    }

    const validReOrder = this.reOrderSettings.validate(isAutoRun);
    const validInventory = this.inventorySettings.validate(isAutoRun);

    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
    } else {
      this.errors = newErrors;
    }

    return this.errors.length === 0 && validReOrder && validInventory;
  }
}

export class ProductReOrderSettingsInputModel extends CoreModel {
  allowReOrder: boolean;
  itemReOrderIntervals: (keyof typeof ItemReOrderIntervals)[];
  discountPercentage: string;
  errors: InputErrorModel<ProductReOrderSettingsInputModel>[];
  constructor(data?: Partial<ProductReOrderSettingsInputModel>) {
    super();
    this.itemReOrderIntervals = (data?.itemReOrderIntervals ||
      []) as (keyof typeof ItemReOrderIntervals)[];
    this.discountPercentage = data?.discountPercentage || "";
    this.allowReOrder = data?.allowReOrder || false;
    this.errors = data?.errors || [];
    autorun(() => {
      this.validate(true);
    });
  }
  hasError(key: keyof ProductReOrderSettingsInputModel): boolean {
    return this.errors.some((error) => error.field === key);
  }
  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<ProductReOrderSettingsInputModel>[] = [];

    if (this.allowReOrder) {
      if (this.itemReOrderIntervals.length === 0) {
        this.addError("itemReOrderIntervals", newErrors);
      }

      this.validateDecimalValue(
        this.discountPercentage,
        "discountPercentage",
        newErrors
      );
      if (
        parseFloat(this.discountPercentage) <= 0 ||
        parseFloat(this.discountPercentage) > 100
      ) {
        this.addError("discountPercentage", newErrors);
      }
    }
    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
    } else {
      this.errors = newErrors;
    }

    return this.errors.length === 0;
  }

  createAPIModel(): IAPIReOrderSettings {
    const data = this.itemReOrderIntervals;
    return {
      discountPercentage: this.allowReOrder
        ? parseFloat(this.discountPercentage)
        : 0,
      reOrderIntervals: data,
    };
  }

  static deserializeAPIModel(
    data: IAPIReOrderSettings
  ): ProductReOrderSettingsInputModel {
    const mappedData: Partial<ProductReOrderSettingsInputModel> = {
      discountPercentage: data.discountPercentage.toString(),
      itemReOrderIntervals: data.reOrderIntervals || [],
      allowReOrder: data.discountPercentage > 0,
    };
    return new ProductReOrderSettingsInputModel(mappedData);
  }
}

export class ProductInventorySettingsInputModel extends CoreModel {
  isTrackInventory: boolean;
  isLimitPurchaseQuantity: boolean;
  isPromote: boolean;
  available: string;
  isInStock: boolean;
  isSellOnOutOfStock: boolean;
  quantityLimit: string;
  errors: InputErrorModel<ProductInventorySettingsInputModel>[];
  hideAvailable: boolean; //Hide “On Hand” option if Variants = Yes.  User will add Inventory on Hand for each Variant.

  constructor(data?: Partial<ProductInventorySettingsInputModel>) {
    super();
    this.isTrackInventory = data?.isTrackInventory || false;
    this.isLimitPurchaseQuantity = data?.isLimitPurchaseQuantity || false;
    this.isPromote = data?.isPromote || false;
    this.available = data?.available || "";
    this.isInStock = data?.isInStock ?? true;
    this.isSellOnOutOfStock = data?.isSellOnOutOfStock || false;
    this.quantityLimit = data?.quantityLimit || "";
    this.errors = data?.errors || [];
    this.hideAvailable = data?.hideAvailable || false;
    autorun(() => {
      this.validate(true);
    });
  }

  hasError(key: keyof ProductInventorySettingsInputModel): boolean {
    return this.errors.some((error) => error.field === key);
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<ProductInventorySettingsInputModel>[] = [];
    if (this.isTrackInventory && !this.hideAvailable) {
      this.validateDecimalValue(this.available, "available", newErrors);
      if (parseFloat(this.available) <= 0) {
        this.addError("available", newErrors);
      }
    }

    if (this.isLimitPurchaseQuantity) {
      this.validateDecimalValue(this.quantityLimit, "quantityLimit", newErrors);
      if (parseFloat(this.quantityLimit) <= 0) {
        this.addError("quantityLimit", newErrors);
      }
    }

    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
    } else {
      this.errors = newErrors;
    }

    return this.errors.length === 0;
  }

  createAPIModel(): IAPIInventorySettings {
    return {
      isInStock: this.isInStock,
      available: parseFloat(this.available) || 0,
      isSellOnOutOfStock: this.isSellOnOutOfStock,
      isPromote: this.isPromote,
      isLimitPurchaseQuantity: this.isLimitPurchaseQuantity,
      quantityLimit: this.isLimitPurchaseQuantity
        ? parseFloat(this.quantityLimit)
        : 0,
    };
  }
  static deserializeAPIModel(
    data: IAPIInventorySettings,
    hasVariants: boolean
  ): ProductInventorySettingsInputModel {
    const mappedData: Partial<ProductInventorySettingsInputModel> = {
      available: data.available.toString(),
      isSellOnOutOfStock: data.isSellOnOutOfStock,
      isPromote: data.isPromote,
      isLimitPurchaseQuantity: data.isLimitPurchaseQuantity,
      quantityLimit: data.quantityLimit.toString(),
      hideAvailable: hasVariants,
      isInStock: data.isInStock,
    };
    return new ProductInventorySettingsInputModel(mappedData);
  }
}
